import { Domain, TrendData, VariantInfo } from "../types";
import { getStoredToken } from "../contexts/AuthContext";
import { publishToast } from "./toastBus";
import { MyLocalStorage } from "../utils/storage";

// API service for keyword cluster operations
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Stable subscription error codes emitted by the BE billing gate. Keep in sync with seo_keyword_cluster_generator/tenant.py.
export const SUBSCRIPTION_ERROR_CODES = {
  PAST_DUE: "subscription_past_due",
  CANCELED: "subscription_canceled",
  INACTIVE: "subscription_inactive",
  NO_SUB: "subscription_no_sub",
} as const;

export type SubscriptionErrorCode =
  (typeof SUBSCRIPTION_ERROR_CODES)[keyof typeof SUBSCRIPTION_ERROR_CODES];

// User-facing copy for each subscription error code. Centralized so toasts and the billing page stay consistent.
const SUBSCRIPTION_TOAST_MESSAGES: Record<SubscriptionErrorCode, string> = {
  [SUBSCRIPTION_ERROR_CODES.PAST_DUE]:
    "Your payment is past due. The app is in read-only mode until you update your payment method.",
  [SUBSCRIPTION_ERROR_CODES.CANCELED]:
    "Your subscription is canceled. Choose a plan to restore access.",
  [SUBSCRIPTION_ERROR_CODES.INACTIVE]:
    "Your account has been deactivated. Contact support to restore access.",
  [SUBSCRIPTION_ERROR_CODES.NO_SUB]:
    "Your subscription setup is incomplete. Choose a plan to continue.",
};

const BILLING_PAGE_PATH = "/admin?tab=billing";
const ACCOUNT_DEACTIVATED_PATH = "/account-deactivated";

const isOnBillingPage = (): boolean => {
  // Both pathname AND query must match — billing UI lives at /admin?tab=billing.
  return (
    window.location.pathname === "/admin" &&
    new URLSearchParams(window.location.search).get("tab") === "billing"
  );
};

// Endpoints that don't require authentication
const PUBLIC_ENDPOINTS = [
  "/api/auth/login",
  "/api/auth/signup",
  "/api/auth/signup/verify",
  "/api/auth/forgot-password",
  "/api/auth/reset-password",
  "/api/auth/invite/validate",
  "/api/auth/invite/accept",
  "/api/auth/verify-email",
  "/api/public/plans",
  "/api/public/contact",
];

/**
 * Centralized fetch wrapper (interceptor) that:
 * 1. Automatically adds Authorization header for authenticated endpoints
 * 2. Handles 401 responses by clearing token and redirecting to login
 * 3. Provides consistent error handling
 */
const apiRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const url = `${API_BASE_URL}${endpoint}`;
  const isPublicEndpoint = PUBLIC_ENDPOINTS.some((publicPath) =>
    endpoint.startsWith(publicPath)
  );

  // Build headers - add auth token for non-public endpoints
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (!isPublicEndpoint) {
    const token = getStoredToken();
    if (token) {
      (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // 401, 402, and 403 (with subscription_inactive) trigger global side effects: token clearing, navigation, and a toast. Other statuses pass through and are surfaced as ApiError by apiRequestJson, so per-call catch blocks can decide what to do.
  if (response.status === 401 || response.status === 402 || response.status === 403) {
    await handleAuthOrBillingError(response);
  }

  return response;
};

/**
 * Read the JSON error body once and dispatch on status + stable `code`. May redirect, clear the auth token, and/or publish a toast. Always throws an `ApiError` carrying both the HTTP status and the BE code so callers can branch if needed.
 */
const handleAuthOrBillingError = async (response: Response): Promise<never> => {
  // Body may be empty (e.g. some proxies) or non-JSON; treat both as "no code".
  const errorData: { detail?: string; code?: string } = await response
    .json()
    .catch(() => ({}));
  const code = errorData.code;
  const detail = errorData.detail || `HTTP error! status: ${response.status}`;

  if (response.status === 401) {
    MyLocalStorage.jwt_token.remove();
    window.location.href = "/login";
    throw new ApiError(401, "Unauthorized", code);
  }

  if (response.status === 402) {
    // 402 is the BE billing gate signalling "writes blocked / no active subscription". Show targeted copy when we have a code, fall back to a generic message otherwise. Skip the redirect when the user is already on the billing page (they're trying to fix it; bouncing them would wipe their state).
    const message =
      (code && SUBSCRIPTION_TOAST_MESSAGES[code as SubscriptionErrorCode]) ||
      detail;
    publishToast({ type: "warning", message, duration: 6000 });
    if (!isOnBillingPage()) {
      window.location.href = BILLING_PAGE_PATH;
    }
    throw new ApiError(402, message, code);
  }

  // status === 403
  if (code === SUBSCRIPTION_ERROR_CODES.INACTIVE) {
    // Tenant is deactivated — there is nothing actionable in-app. Clear credentials and route to a dedicated landing page that explains the state and points to support.
    MyLocalStorage.jwt_token.remove();
    publishToast({
      type: "error",
      message: SUBSCRIPTION_TOAST_MESSAGES[SUBSCRIPTION_ERROR_CODES.INACTIVE],
      duration: 8000,
    });
    window.location.href = ACCOUNT_DEACTIVATED_PATH;
    throw new ApiError(403, detail, code);
  }
  // Other 403s (RBAC, ownership) are not global concerns; let callers handle.
  throw new ApiError(403, detail, code);
};

/**
 * Helper function for API requests that need JSON response parsing
 * with consistent error handling
 */
const apiRequestJson = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await apiRequest(endpoint, options);

  if (!response.ok) {
    const errorData: { detail?: string; code?: string } = await response
      .json()
      .catch(() => ({}));
    throw new ApiError(
      response.status,
      errorData.detail || `HTTP error! status: ${response.status}`,
      errorData.code,
    );
  }

  return await response.json();
};

export interface AnchorKeywordData {
  anchor_keyword: string;
  cluster_size: number;
  cluster_id: string;
  created_at: string;
  total_search_volume: number;
  keywords_ranking: number;
  location_code: number;
  language_code: string;
  location_name: string;
  language_name: string;
  country_iso_code: string | null;
}

export interface CreateClusterPayload {
  anchor_keyword: string;
  temperature?: number;
  max_number_of_keywords?: number;
  location_code: number;
  language_code: string;
}

export interface KeywordVariant {
  variant: string;
  relevance_score: number;
  is_tracked: boolean;
  semantic_similarity?: number;
  length_score?: number;
  intent_score?: number;
  keyword_overlap?: number;
  openai_similarity?: number;
}
export interface CreateClusterResponse {
  anchor_keyword: string;
  variants: VariantInfo[];
  cluster_size: number;
  location_code: number;
  language_code: string;
}

export interface SelectedVariantsPayload {
  anchor_keyword: string;
  variants: KeywordVariant[];
  location_code: number;
  language_code: string;
}

export interface ProcessVariantsResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export interface RefreshClusterPayload {
  anchor_keyword: string;
  location_code: number;
  language_code: string;
}

export interface DeleteAnchorKeywordResponse {
  message?: string;
}

export interface DeleteKeywordsPayload {
  anchor_keyword: string;
  keywords: string[];
  location_code: number;
  language_code: string;
}

export interface DeleteKeywordsResponse {
  success: boolean;
  message: string;
  deleted_keywords: string[];
}

export interface AddKeywordsPayload {
  anchor_keyword: string;
  keywords: string[];
  location_code: number;
  language_code: string;
}

export interface EnlargeClusterPayload {
  anchor_keyword: string;
  target_cluster_size: number;
  temperature: number;
  location_code: number;
  language_code: string;
}

export interface AddKeywordsResponse {
  success: boolean;
  message: string;
  added_keywords: string[];
}

export interface EnlargeClusterResponse {
  anchor_keyword: string;
  variants: VariantInfo[];
  cluster_size: number;
}

export interface ConfirmEnlargeClusterPayload {
  anchor_keyword: string;
  new_variants: VariantInfo[];
  location_code: number;
  language_code: string;
}

export interface TopicSuggestionsPayload {
  keyword: string;
  num_suggestions: number;
}

export interface TopicSuggestion {
  title: string;
  description: string;
}

export interface TopicSuggestionsResponse {
  keyword: string;
  suggestions: TopicSuggestion[];
}

export interface LocationItem {
  location_code: number;
  location_name: string;
  country_iso_code?: string;
}

export interface LanguageItem {
  language_code: string;
  language_name: string;
}

export interface SerpPreviewPayload {
  keyword: string;
  anchor_keyword: string;
  client_id: string;
  location_code: number;
  language_code: string;
}

export interface RankChanges {
  previous_rank_absolute: number | null;
  is_new: boolean;
  is_up: boolean;
  is_down: boolean;
}

export interface SerpResult {
  resultType: string;
  url: string;
  title: string;
  domain?: string;
  description?: string;
  rank_group?: number;
  rank_absolute?: number;
  rank_changes?: RankChanges | null;
}

export interface SerpSnapshot {
  date: string;
  your_rank: number | null;
  results: SerpResult[];
  total_results: number;
}

export interface SerpPreviewResponse {
  keyword_id: string;
  keyword: string;
  date_range: string;
  snapshots: SerpSnapshot[];
  total_snapshots: number;
  fetched_at: string;
}

export interface ClusterDataPayload {
  anchor_keyword: string;
  location_code: number;
  language_code: string;
}

export interface ClusterKeywordData {
  keyword: string;
  volume: number;
  difficulty: number;
  ranking: number;
  trend: TrendData;
  relevance_score?: number;
  semantic_similarity?: number;
  length_score?: number;
  intent_score?: number;
  keyword_overlap?: number;
  openai_similarity?: number;
  ranking_url?: string;
  // True when `ranking` may be out of date (older than the cache TTL, or no
  // rank check has ever succeeded). Absent from older responses = not stale.
  rank_is_stale?: boolean;
}

/**
 * Computed volume-weighted "center" of a cluster — the diamond marker on the
 * bubble chart. Not a real keyword. `volume` (Y) and `difficulty` (X) are
 * averages (float); `ranking` feeds the bubble size (rounded int). `trend`
 * carries week/month/life deltas whose values may be null → rendered N/A.
 * The response `center` is null for an empty cluster.
 */
export interface ClusterCenterData {
  keyword: string;
  volume: number | null;
  difficulty: number | null;
  ranking: number | null;
  trend: TrendData | null;
  is_center: boolean;
}

export interface KeywordReplacementUsage {
  used: number;
  limit: number | null; // null = unlimited
  resets_at: string | null; // ISO timestamp of the next subscription renewal (when the cap resets)
}

export interface ClusterDataResponse {
  anchor_keyword: string;
  cluster_size: number;
  visualization_data: ClusterKeywordData[];
  // Earliest fetch time among the cluster's keywords (data-freshness stamp).
  data_fetched_at: string | null;
  summary: {
    total_keywords: number;
    keywords_ranking: number;
    keywords_not_ranking: number;
    total_search_volume: number;
    avg_search_volume: number;
    avg_difficulty: number;
    avg_rank: number;
    best_rank: number;
    worst_rank: number;
  };
  usage?: KeywordReplacementUsage | null;
  // Volume-weighted cluster center (diamond marker); null for an empty cluster.
  center?: ClusterCenterData | null;
}

export interface TenantInfo {
  id: string;
  name: string;
  is_active: boolean;
  serp_preview_enabled: boolean;
  subscription_plan_name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

// Subscription Plan types
export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  yearly_price: number;
  max_domains: number;
  max_clusters: number;
  max_keywords_per_cluster: number;
  max_users: number;
  stripe_product_id?: string | null;  // Stripe Product ID (e.g., prod_xxx)
  stripe_price_id?: string | null;  // Stripe Price ID for billing (legacy/fallback)
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface SubscriptionPlanCreate {
  name: string;
  price: number;
  yearly_price: number;
  max_domains: number;
  max_clusters: number;
  max_keywords_per_cluster: number;
  max_users: number;
  stripe_product_id?: string;  // Stripe Product ID (e.g., prod_xxx)
  stripe_price_id?: string;  // Stripe Price ID for billing (legacy)
  is_active?: boolean;
}

export interface SubscriptionPlanUpdate {
  name?: string;
  price?: number;
  yearly_price?: number;
  max_domains?: number;
  max_clusters?: number;
  max_keywords_per_cluster?: number;
  max_users?: number;
  stripe_product_id?: string;  // Stripe Product ID (e.g., prod_xxx)
  stripe_price_id?: string;  // Stripe Price ID for billing (legacy)
  is_active?: boolean;
}

export interface TenantUsageStats {
  domains_used: number;
  domains_limit: number | null;  // null = unlimited (system tenant)
  clusters_used: number;
  clusters_limit: number | null;  // null = unlimited (system tenant)
  users_used: number;
  users_limit: number | null;  // null = unlimited (system tenant)
  max_keywords_per_cluster: number | null;  // null = unlimited (system tenant)
  // Per-tenant cluster-replacement cap (per billing period; resets on subscription renewal)
  cluster_replacements_used?: number;
  cluster_replacements_limit?: number | null;  // null = unlimited
  cluster_replacements_resets_at?: string | null;  // ISO timestamp of the next subscription renewal
  is_unlimited?: boolean;  // true for system tenant (Scopic)
}

// Alias for clarity in client-side code
export type MyTenantUsage = TenantUsageStats;

// Admin API types
export interface AdminUser {
  id: string;
  email: string;
  tenant_id: string;
  tenant_name?: string;
  role: string;
  is_active: boolean;
  is_superadmin: boolean;
  invite_pending?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AdminTenant {
  id: string;
  name: string;
  is_active: boolean;
  serp_preview_enabled: boolean;
  user_count: number;
  subscription_plan_id?: string | null;
  subscription_plan_name?: string | null;
  // Stripe billing status
  subscription_status?: string | null;  // active, trialing, past_due, canceled, or null
  stripe_customer_id?: string | null;
  billing_cycle?: string | null;  // monthly, yearly, or null
  usage_stats?: TenantUsageStats | null;
  subscription_current_period_end?: string | null;
  last_activity_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface TenantUser {
  id: string;
  email: string;
  tenant_id: string;
  role: string;
  is_active: boolean;
  invite_pending?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface TenantUserCreatePayload {
  email: string;
  password?: string;
  tenant_id: string;
  role: string;
  is_active?: boolean;
}

export interface TenantUserUpdatePayload {
  email?: string;
  role?: string;
  is_active?: boolean;
}

export interface CreateUserPayload {
  email: string;
  tenant_id: string;
  role: string;
  is_superadmin: boolean;
}

export interface UpdateUserPayload {
  email?: string;
  tenant_id?: string;
  role?: string;
  is_active?: boolean;
  is_superadmin?: boolean;
  new_password?: string;
}

export interface CreateTenantPayload {
  name: string;
  is_active: boolean;
}

export interface UpdateTenantPayload {
  name?: string;
  is_active?: boolean;
  serp_preview_enabled?: boolean;
}

// Grant/Revoke free access types
export interface GrantFreeAccessPayload {
  plan_id: string;
  duration_months?: number | null; // null = forever
}

export interface GrantFreeAccessResponse {
  tenant_id: string;
  tenant_name: string;
  plan_name: string;
  subscription_status: string;
  duration: string;
  message: string;
}

// === Keyword History (animation timeline) ===

/** Metrics for a single keyword at one snapshot date. */
export interface KeywordHistoryItem {
  keyword: string;
  volume: number;
  difficulty: number;
  ranking: number;
  ranking_url: string | null;
  /** True when no real fetch occurred on this date; metrics are carried forward from the prior fetch. */
  is_forward_filled: boolean;
  /**
   * True when the rank alone was carried forward (no rank fetch on this date,
   * e.g. a failed SERP lookup while volume was fetched). Absent = false.
   */
  rank_is_forward_filled?: boolean;
}

/** All keywords' metrics captured on a single snapshot date. */
export interface KeywordHistorySnapshot {
  date: string; // ISO date string, e.g. "2024-03-18"
  keywords: KeywordHistoryItem[];
}

/** Full historical timeline returned by GET /api/cluster/{client}/{anchor}/history. */
export interface KeywordHistoryResponse {
  anchor_keyword: string;
  snapshots: KeywordHistorySnapshot[];
}

/** Response from GET /api/keywords/last-global-refresh */
export interface LastGlobalRefreshResponse {
  last_refresh_at: string | null;
}

export class ApiError extends Error {
  // `code` carries the BE's stable error-code field (e.g. "subscription_past_due") when present. UI components branch on this rather than parsing the human-readable `message`.
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const clusterApi = {
  async createCluster(
    payload: CreateClusterPayload
  ): Promise<CreateClusterResponse> {
    return apiRequestJson("/api/cluster/create", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async processSelectedVariants(
    client: string,
    payload: SelectedVariantsPayload
  ): Promise<ProcessVariantsResponse> {
    return apiRequestJson(`/api/cluster/${client}/confirm`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async getAnchorKeywords(client: string): Promise<AnchorKeywordData[]> {
    return apiRequestJson(`/api/anchor-keywords/${client}`, {
      method: "GET",
    });
  },

  async getClusterData(
    client: string,
    payload: ClusterDataPayload
  ): Promise<ClusterDataResponse> {
    const params = new URLSearchParams({
      language_code: payload.language_code,
      location_code: String(payload.location_code),
    });
    return apiRequestJson(
      `/api/cluster/${client}/${encodeURIComponent(payload.anchor_keyword)}?${params}`,
      { method: "GET" },
    );
  },

  async refreshCluster(
    client: string,
    payload: RefreshClusterPayload
  ): Promise<ClusterDataResponse> {
    return apiRequestJson(`/api/cluster/${client}/refresh`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  /**
   * Fetch real historical snapshots for all keywords in a cluster over the
   * given date range.  Used by the Keyword Trends Animation page to replace
   * the random-drift simulation with actual DB data.
   */
  async getKeywordHistory(
    client: string,
    anchorKeyword: string,
    dateFrom: string,
    dateTo: string,
    languageCode: string = "en",
    locationCode: number = 2840,
  ): Promise<KeywordHistoryResponse> {
    const params = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
      language_code: languageCode,
      location_code: String(locationCode),
    });
    return apiRequestJson(
      `/api/cluster/${client}/${encodeURIComponent(anchorKeyword)}/history?${params}`,
      { method: "GET" },
    );
  },

  async deleteAnchorKeyword(
    client: string,
    anchorKeyword: string,
    languageCode: string = "en",
    locationCode: number = 2840,
  ): Promise<DeleteAnchorKeywordResponse> {
    const params = new URLSearchParams({
      language_code: languageCode,
      location_code: String(locationCode),
    });
    return apiRequestJson(
      `/api/cluster/${client}/${encodeURIComponent(anchorKeyword)}?${params}`,
      { method: "DELETE" },
    );
  },

  async deleteKeywords(
    client: string,
    payload: DeleteKeywordsPayload
  ): Promise<DeleteKeywordsResponse> {
    return apiRequestJson(`/api/${client}/delete-keywords`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async addKeywords(
    client: string,
    payload: AddKeywordsPayload
  ): Promise<AddKeywordsResponse> {
    return apiRequestJson(`/api/${client}/add-keywords`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async enlargeCluster(
    client: string,
    payload: EnlargeClusterPayload
  ): Promise<EnlargeClusterResponse> {
    return apiRequestJson(`/api/cluster/${client}/enlarge`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async confirmEnlargeCluster(
    client: string,
    payload: ConfirmEnlargeClusterPayload
  ) {
    return apiRequestJson(`/api/cluster/${client}/enlarge/confirm`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async getTrackedKeywordsCount(): Promise<{ tracked_keywords_count: number }> {
    return apiRequestJson("/api/tracked-keywords/count");
  },

  async getCurrentTenant(): Promise<TenantInfo> {
    return apiRequestJson("/api/tenant/current");
  },
  
  async getMyUsage(): Promise<TenantUsageStats> {
    return apiRequestJson("/api/tenant/usage");
  },

  // Client management methods
  async createClient(payload: {
    name: string;
    domain?: string;
  }): Promise<{ id: string; name: string; domain?: string }> {
    return apiRequestJson("/api/client", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async getClients(): Promise<Domain[]> {
    return apiRequestJson("/api/clients", {
      method: "GET",
    });
  },

  async updateClient(
    clientId: string,
    payload: { name?: string; domain?: string },
  ): Promise<{ id: string; name?: string; domain?: string }> {
    return apiRequestJson(`/api/client/${clientId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },

  async deleteClient(clientId: string): Promise<{ detail: string }> {
    return apiRequestJson(`/api/client/${clientId}`, {
      method: "DELETE",
    });
  },

  async getTopicSuggestions(
    payload: TopicSuggestionsPayload
  ): Promise<TopicSuggestionsResponse> {
    return apiRequestJson("/api/keyword/suggestions", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async getSerpPreview(
    payload: SerpPreviewPayload
  ): Promise<SerpPreviewResponse> {
    return apiRequestJson("/api/keyword/serp-preview", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async getLocations(): Promise<LocationItem[]> {
    return apiRequestJson("/api/reference/locations", { method: "GET" });
  },

  async getLanguages(): Promise<LanguageItem[]> {
    return apiRequestJson("/api/reference/languages", { method: "GET" });
  },

  async getLastGlobalRefresh(): Promise<LastGlobalRefreshResponse> {
    return apiRequestJson("/api/keywords/last-global-refresh", {
      method: "GET",
    });
  },
};

// Admin API for superadmin operations
export const adminApi = {
  // User management
  async listUsers(): Promise<AdminUser[]> {
    return apiRequestJson("/api/admin/users");
  },

  async createUser(payload: CreateUserPayload): Promise<AdminUser> {
    return apiRequestJson("/api/admin/users", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async updateUser(
    userId: string,
    payload: UpdateUserPayload
  ): Promise<AdminUser> {
    return apiRequestJson(`/api/admin/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  async deleteUser(userId: string): Promise<{ detail: string }> {
    return apiRequestJson(`/api/admin/users/${userId}`, {
      method: "DELETE",
    });
  },

  async resendInvite(userId: string): Promise<{ detail: string }> {
    return apiRequestJson(`/api/admin/users/${userId}/resend-invite`, {
      method: "POST",
    });
  },

  async loginAs(userId: string): Promise<{ access_token: string; token_type: string }> {
    return apiRequestJson(`/api/auth/preview-as/${userId}`, {
      method: "POST",
    });
  },

  // Tenant management
  async listTenants(): Promise<AdminTenant[]> {
    return apiRequestJson("/api/admin/tenants");
  },

  async createTenant(payload: CreateTenantPayload): Promise<AdminTenant> {
    return apiRequestJson("/api/admin/tenants", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async updateTenant(
    tenantId: string,
    payload: UpdateTenantPayload
  ): Promise<TenantInfo> {
    return apiRequestJson(`/api/tenant/${tenantId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  async deleteTenant(tenantId: string): Promise<{ detail: string }> {
    return apiRequestJson(`/api/tenant/${tenantId}`, {
      method: "DELETE",
    });
  },

  // Free access management (superadmin only)
  async grantFreeAccess(
    tenantId: string,
    payload: GrantFreeAccessPayload
  ): Promise<GrantFreeAccessResponse> {
    return apiRequestJson(`/api/admin/tenants/${tenantId}/grant-free-access`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async revokeFreeAccess(tenantId: string): Promise<{ detail: string; tenant_id: string }> {
    return apiRequestJson(`/api/admin/tenants/${tenantId}/revoke-access`, {
      method: "POST",
    });
  },

  async refreshMetrics(): Promise<MetricsRefreshResult> {
    return apiRequestJson("/api/admin/refresh-metrics", {
      method: "POST",
    });
  },
};

export interface MetricsRefreshResult {
  detail: string;
  // Normal run fields
  clusters_refreshed?: number;
  domains_scanned?: number;
  errors?: number;
  duration_s?: number;
  // Skipped (24-hour guard) fields
  skipped?: boolean;
  reason?: string;
  last_refresh_at?: string;
  last_run_by_email?: string | null;
}

// === Activity / Telemetry Types ===

export interface TenantActivityStats {
  clusters_created: number;
  clusters_refreshed: number;
  clusters_deleted: number;
  keywords_generated_events: number;
  total_keywords_generated: number;
  domains_created: number;
  domains_updated: number;
  domains_deleted: number;
  last_activity_at: string | null;
}

export interface AdminTenantActivityStats extends TenantActivityStats {
  tenant_id: string;
  tenant_name: string;
}

/** A single activity event with user attribution. */
export interface ActivityEvent {
  id: string;
  event_type: string;
  event_label: string;
  user_id: string | null;
  user_email: string | null;
  detail: Record<string, unknown> | null;
  created_at: string | null;
}

export interface ActivityEventsResponse {
  events: ActivityEvent[];
  limit: number;
  offset: number;
}

export interface AdminActivityEventsResponse extends ActivityEventsResponse {
  tenant_id: string;
  tenant_name: string;
}

// Activity API
export const activityApi = {
  /** Get aggregated activity stats for the current tenant. */
  async getMyActivity(): Promise<TenantActivityStats> {
    return apiRequestJson("/api/tenant/activity");
  },

  /** Get recent activity events list for the current tenant. */
  async getMyActivityEvents(limit = 50, offset = 0): Promise<ActivityEventsResponse> {
    return apiRequestJson(`/api/tenant/activity/events?limit=${limit}&offset=${offset}`);
  },

  /** Get aggregated activity stats for a specific tenant (superadmin only). */
  async getTenantActivity(tenantId: string): Promise<AdminTenantActivityStats> {
    return apiRequestJson(`/api/admin/tenants/${tenantId}/activity`);
  },

  /** Get recent activity events for a specific tenant (superadmin only). */
  async getTenantActivityEvents(
    tenantId: string,
    limit = 50,
    offset = 0
  ): Promise<AdminActivityEventsResponse> {
    return apiRequestJson(
      `/api/admin/tenants/${tenantId}/activity/events?limit=${limit}&offset=${offset}`
    );
  },
};

// Tenant admin API (tenant-scoped user visibility)
export const tenantUserApi = {
  async listUsers(): Promise<TenantUser[]> {
    return apiRequestJson("/api/users");
  },
  async createUser(payload: TenantUserCreatePayload): Promise<TenantUser> {
    return apiRequestJson("/api/user", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  async updateUser(
    userId: string,
    payload: TenantUserUpdatePayload
  ): Promise<TenantUser> {
    return apiRequestJson(`/api/user/${userId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },
  async deleteUser(userId: string): Promise<{ detail: string }> {
    return apiRequestJson(`/api/user/${userId}`, {
      method: "DELETE",
    });
  },
  async resendInvite(userId: string): Promise<{ detail: string }> {
    return apiRequestJson(`/api/user/${userId}/resend-invite`, {
      method: "POST",
    });
  },
};

// Subscription Plans API (superadmin only)
export const subscriptionApi = {
  // Plan management
  async listPlans(includeInactive: boolean = false): Promise<SubscriptionPlan[]> {
    const params = includeInactive ? "?include_inactive=true" : "";
    return apiRequestJson(`/api/admin/subscription-plans${params}`);
  },

  async getPlan(planId: string): Promise<SubscriptionPlan> {
    return apiRequestJson(`/api/admin/subscription-plans/${planId}`);
  },

  async createPlan(payload: SubscriptionPlanCreate): Promise<SubscriptionPlan> {
    return apiRequestJson("/api/admin/subscription-plans", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async updatePlan(
    planId: string,
    payload: SubscriptionPlanUpdate
  ): Promise<SubscriptionPlan> {
    return apiRequestJson(`/api/admin/subscription-plans/${planId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  async deletePlan(planId: string): Promise<{ detail: string }> {
    return apiRequestJson(`/api/admin/subscription-plans/${planId}`, {
      method: "DELETE",
    });
  },

  async getTenantUsage(tenantId: string): Promise<TenantUsageStats> {
    return apiRequestJson(`/api/admin/tenants/${tenantId}/usage`);
  },

  // Stripe price management
  async getStripePrices(planId: string): Promise<PlanStripePricesResponse> {
    return apiRequestJson(`/api/admin/subscription-plans/${planId}/stripe-prices`);
  },

  async createOrUpdatePrice(
    planId: string,
    payload: CreatePriceRequest
  ): Promise<CreatePriceResponse> {
    return apiRequestJson(`/api/admin/subscription-plans/${planId}/prices`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
};

// Billing types
export interface BillingStatus {
  subscription_status: string | null;  // active, trialing, past_due, canceled, or null
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_end: string | null;
  current_plan_name: string | null;
  is_system_tenant: boolean;
  trial_end: string | null;
  is_trialing: boolean;
}

export interface StripePriceInfo {
  price_id: string;
  amount: number;  // Amount in cents
  currency: string;
  interval: string;  // "month" or "year"
}

export interface AvailablePlan {
  id: string;
  name: string;
  price: number;  // Display price (monthly) from our DB
  yearly_price: number;  // Display price (yearly) from our DB
  max_domains: number;
  max_clusters: number;
  max_keywords_per_cluster: number;
  max_users: number;
  stripe_product_id?: string | null;
  // Dynamic prices fetched from Stripe via lookup keys
  stripe_monthly_price?: StripePriceInfo | null;
  stripe_yearly_price?: StripePriceInfo | null;
  // Legacy field for backwards compatibility
  stripe_price_id?: string | null;
}

export interface AvailablePlansResponse {
  plans: AvailablePlan[];
  trial_period_days: number;
}

export interface CheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

export interface PortalSessionResponse {
  portal_url: string;
}

export interface CheckoutSessionRequest {
  plan_id: string;  // Internal plan ID (e.g., plan_xxx)
  billing_cycle: "monthly" | "yearly";  // Billing cycle
  success_url?: string;
  cancel_url?: string;
}

// Price management types (superadmin)
export interface PlanStripePricesResponse {
  plan_id: string;
  plan_name: string;
  monthly_price: StripePriceInfo | null;
  yearly_price: StripePriceInfo | null;
}

export interface CreatePriceRequest {
  billing_cycle: "monthly" | "yearly";
  amount: number;  // Amount in cents (e.g., 2900 for $29.00)
  currency?: string;  // Default: "usd"
}

export interface CreatePriceResponse {
  price_id: string;
  lookup_key: string;
  amount: number;
  currency: string;
  interval: string;
  message: string;
}

// Billing API (tenant admin only)
export const billingApi = {
  async getStatus(): Promise<BillingStatus> {
    return apiRequestJson("/api/billing/status");
  },

  async getAvailablePlans(): Promise<AvailablePlansResponse> {
    return apiRequestJson("/api/billing/plans");
  },

  async createCheckoutSession(
    planId: string,
    billingCycle: "monthly" | "yearly" = "monthly"
  ): Promise<CheckoutSessionResponse> {
    const payload: CheckoutSessionRequest = { 
      plan_id: planId, 
      billing_cycle: billingCycle 
    };
    return apiRequestJson("/api/billing/checkout-session", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async createPortalSession(): Promise<PortalSessionResponse> {
    return apiRequestJson("/api/billing/portal-session", {
      method: "POST",
    });
  },
};

// === Public API Types (no auth required) ===

export interface SignupPayload {
  email: string;
  password: string;
  company_name: string;
  plan_id: string;
  billing_cycle: "monthly" | "yearly";
  domain?: string;
}

export interface SignupResponse {
  checkout_url: string;
  session_id: string;
  tenant_id: string;
  user_id: string;
}

export interface SignupVerifyResponse {
  access_token: string;
  token_type: string;
  tenant_id: string;
  user_id: string;
}

// Public API (no authentication required)
export const publicApi = {
  /**
   * Get available subscription plans (public endpoint).
   * Used for pricing pages and self-service signup.
   */
  async getPlans(): Promise<AvailablePlansResponse> {
    const response = await fetch(`${API_BASE_URL}/api/public/plans`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.detail || "Failed to fetch plans"
      );
    }
    return response.json();
  },

  /**
   * Self-service signup - creates tenant, user, and Stripe checkout session.
   * Returns checkout URL for redirect to Stripe.
   */
  async signup(payload: SignupPayload): Promise<SignupResponse> {
    const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.detail || "Signup failed"
      );
    }
    return response.json();
  },

  /**
   * Verify signup after Stripe checkout completion.
   * Returns JWT token for auto-login.
   */
  async verifySignup(sessionId: string): Promise<SignupVerifyResponse> {
    const response = await fetch(
      `${API_BASE_URL}/api/auth/signup/verify?session_id=${encodeURIComponent(sessionId)}`
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.detail || "Verification failed"
      );
    }
    return response.json();
  },

  async contact(name: string, email: string, message: string): Promise<{ detail: string }> {
    const response = await fetch(`${API_BASE_URL}/api/public/contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email, message }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(response.status, errorData.detail || "Failed to send message");
    }
    return response.json();
  },

  async forgotPassword(email: string): Promise<{ detail: string }> {
    const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(response.status, errorData.detail || "Request failed");
    }
    return response.json();
  },

  async validateResetToken(token: string): Promise<{ email: string }> {
    const response = await fetch(
      `${API_BASE_URL}/api/auth/reset-password/validate?token=${encodeURIComponent(token)}`
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(response.status, errorData.detail || "Invalid or expired token");
    }
    return response.json();
  },

  async resetPassword(token: string, password: string): Promise<{ detail: string }> {
    const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(response.status, errorData.detail || "Reset failed");
    }
    return response.json();
  },

  async validateInviteToken(token: string): Promise<{ email: string; tenant_name: string }> {
    const response = await fetch(
      `${API_BASE_URL}/api/auth/invite/validate?token=${encodeURIComponent(token)}`
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(response.status, errorData.detail || "Invalid or expired invitation");
    }
    return response.json();
  },

  async acceptInvite(token: string, password: string): Promise<{ access_token: string; token_type: string }> {
    const response = await fetch(`${API_BASE_URL}/api/auth/invite/accept`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(response.status, errorData.detail || "Failed to accept invitation");
    }
    return response.json();
  },

  async verifyEmail(token: string): Promise<{ detail: string; access_token?: string }> {
    const response = await fetch(`${API_BASE_URL}/api/auth/verify-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(response.status, errorData.detail || "Verification failed");
    }
    return response.json();
  },

  async resendVerificationEmail(): Promise<{ detail: string }> {
    return apiRequestJson("/api/auth/resend-verification", {
      method: "POST",
    });
  },

  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ detail: string }> {
    return apiRequestJson("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
  },

};
